/**
 * VertexEditController — the Edit-sub-mode direct-manipulation drags
 * extracted from ConstructionTool (#846, epic #828). A tool OWNS a
 * controller instance and DRIVES it by composition (it is NOT a base
 * class), mirroring the `RotationController` / `DeleteSliceController`
 * pattern: the controller owns the three vertex-edit drag STATE
 * fields plus the drag-exclusive geometry helpers, while everything
 * construction-specific it needs from the owning tool (scope / scene /
 * project / settings / commit / selection, the shared live-snap
 * machinery, the mirror-routing hit-test, `translateDragClosure`, the
 * per-line live readout + cursor refresh) is injected through the
 * `VertexEditHost` interface.
 *
 * The controller owns three drag-state fields exclusively:
 *   - `vertexDrag`        — dragging an anchor (snap-chain closure
 *                           translation, origin-anchor pulls, #180
 *                           bidirectional-mirror matrices, #684
 *                           close-on-drop);
 *   - `bezierHandleDrag`  — dragging a bezier handle tip (#217, #180);
 *   - `curveDrag`         — bending a curve by dragging its stroke
 *                           (#180 bidirectional).
 *
 * ConstructionTool's `onDown` (the Edit-mode handle / anchor / curve
 * starts) seeds a drag through `maybeStartDrag`; its `onMove` / `onUp`
 * / `onCancel` delegate the three drags, preserving the exact original
 * dispatch order.
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { GuideRay, Scene, SceneSelection } from "../canvas/Scene";
import { projectConstructionLines, type Project } from "../model/Project";
import type { ToolSettings } from "./EditorContext";
import {
  applyMirror,
  isMirrorDerived,
  type MirrorMatrix,
} from "../model/constructionMirror";
import {
  computeVertexDragClosure,
  originAnchorPullsForVertexDrag,
} from "../model/dragClosure";
import {
  hasCoincidentConstraint,
  propagateCoincidentAfterAnchorDrag,
  pruneInvalidCenterConstraints,
  type AnchorRefLite,
  type SnapRefLite,
} from "../model/constraintEnforcement";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isGridSnapActive,
  isPointConstraintActive,
  snapPointToGrid,
  snapToCommonAngle,
} from "./dragSnap";
import {
  dropConstructionPrimitive,
  settleSnapReshapedLines,
} from "../model/constructionLineNormalize";
import {
  isPinnableSnapRef,
  snapRefToConstraintRef,
  MIRROR_OFFSET_REFUSE_HINT,
} from "./constructionSnapRefs";
import { newId } from "../model/ids";
import { translateClosureInProject } from "./SelectTool";
import { showCanvasToast } from "../canvasToast";
import { setAngleHint } from "../snapAngleIndicator";
import {
  applyCurveBend,
  mirroredHandleFor as sharedMirroredHandleFor,
} from "./outlineEditGeometry";
import { closeSnapRadius } from "./drawGesture";
import { setCanvasCursor } from "../canvas/canvasCursor";
import type { LiveSnapPair } from "./constructionSnapController";
import type { HandleHit } from "./deleteSliceController";

/** A curve hit-test result (from `VertexEditHost.hitConstructionCurve`). */
export interface VertexCurveHit {
  constructionId: string;
  pathIndex: number;
  curveIndex: number;
  curve: paper.Curve;
  t: number;
}

/** Everything the VertexEditController needs from its owning tool. The
 *  three drag-state fields + the drag-exclusive geometry helpers live
 *  in the controller; the shared machinery (snap tiers, mirror routing,
 *  closure translation, live readouts, cursor refresh) stays on
 *  ConstructionTool and is injected through this host. */
export interface VertexEditHost {
  // ---- core context -------------------------------------------------
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  getSettings(): ToolSettings;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: SceneSelection): void;

  // ---- shared live-snap machinery -----------------------------------
  getLiveSnap(): LiveSnapPair | null;
  clearLiveSnap(): void;
  refreshLiveSnapOverlay(): void;
  updateLiveSnap(
    sources: Array<{ ref: SnapRefLite; pos: { x: number; y: number } }>,
    extraExcludedLineIds?: ReadonlySet<string>,
  ): { sourceRef: SnapRefLite; candidatePos: { x: number; y: number } } | null;
  findLineSnap(
    p: { x: number; y: number },
    excludeId?: string,
  ): { x: number; y: number } | null;
  /** Nearest crossing of two visible lines (#128) — more specific than a point
   *  along one, so callers try it first. Same Line-snap gate. */
  findIntersectionSnap(
    p: { x: number; y: number },
    excludeId?: string,
  ): { x: number; y: number } | null;

  // ---- shared helpers (stay on ConstructionTool) --------------------
  /** Rigidly translate every line in a pre-computed drag closure —
   *  SHARED with group-drag and line-drag, so it stays on the tool. */
  translateDragClosure(
    closure: ReadonlySet<string>,
    originIds: ReadonlySet<string>,
    dx: number,
    dy: number,
  ): void;
  /** Push the live length / angle of a Line-primitive line into the
   *  per-line panel during a vertex drag (tool-panel wiring). */
  notifyLinePrimitiveLiveUpdate(
    constructionId: string,
    scope: paper.PaperScope,
  ): void;
  /** True for any derived (offset OR mirror) line. */
  isDerivedLineId(id: string): boolean;
  /** Route a drag that started on a mirror-derived line back to the
   *  source root (issue #180). Null when the chain hits an offset. */
  tryRouteMirrorLineDragToSource(
    hitLineId: string,
  ): { sourceRootId: string; matrices: MirrorMatrix[] } | null;
  /** Nearest visible construction curve under a point, or null. */
  hitConstructionCurve(pt: paper.Point): VertexCurveHit | null;
  /** Re-pick the edit-mode cursor after a drag completes. */
  refreshEditCursor(p: { x: number; y: number }): void;
  /** Capture a smooth-toggle pending tap (the smooth-toggle gesture
   *  lives on the tool; an anchor press in that mode defers to it). */
  setSmoothTogglePending(p: { x: number; y: number }): void;
}

export class VertexEditController {
  // ---- exclusively-owned drag state ---------------------------------

  // Vertex-edit drag state — set in "edit" sub-mode when the user
  // clicks a handle on an existing line. Holds the line id and which
  // segment to mutate; on commit we read the live (already-mutated)
  // pathData off the rendered item.
  private vertexDrag: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    /** Pre-drag anchor position. Used at commit to compute the
     *  total drag delta `lastAnchorPos − startAnchorPos`, which
     *  drives the closure rigid-translation pass on commit. */
    startAnchorPos: { x: number; y: number };
    /** Last frame's live anchor position. Used to compute the
     *  frame-over-frame delta the live chain-translation pass
     *  applies to every snap-connected partner line. Initialised
     *  to the anchor's pre-drag position when the drag starts. */
    lastAnchorPos: { x: number; y: number };
    /** Lines that rigidly translate with this vertex drag — every
     *  snap-chain partner of the dragged anchor plus their offset
     *  descendants. Computed once at mouseDown by
     *  `computeVertexDragClosure`; the dragged line itself and its
     *  own offsets are excluded (path-edit + `rederiveOffsetsOf`
     *  handle them separately). */
    closure: Set<string>;
    /** Anchors on the dragged line (other than the dragged one)
     *  that are snap-pinned to anchors on `closure` lines — they
     *  get pulled along by the frame delta so the coincident pair
     *  stays satisfied. Pre-computed at mouseDown by
     *  `originAnchorPullsForVertexDrag`. */
    originAnchorPulls: Array<{ pathIndex: number; segmentIndex: number }>;
    /** Issue #180 bidirectional vertex editing. When set, the user
     *  grabbed an anchor on a mirror-derived line; the drag's
     *  `constructionId` was redirected to the source ROOT, and the
     *  per-frame cursor position gets fed through this matrix
     *  chain (outermost-first) to get the source-space target the
     *  source's anchor should be moved to. Snap is intentionally
     *  bypassed for bidirectional vertex drags in V1 — snap
     *  candidates reference the mirror's anchor neighbours which
     *  would need a separate mirror-space scan; deferred to a
     *  follow-up. */
    bidirectionalMirrorMatrices?: MirrorMatrix[];
  } | null = null;

  // Bezier-handle drag state — set in "edit" sub-mode when the user
  // clicks one of the hollow tangent endpoints. Mutates a single side
  // (`handleIn` or `handleOut`) of one segment so the user can shape
  // each tangent independently.
  private bezierHandleDrag: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    side: "in" | "out";
    /** Bidirectional editing (issue #180): when the gesture started
     *  on a mirror-derived line, `constructionId` was swapped to the
     *  source root. Per-frame the cursor is mapped through this
     *  chain (outer-to-inner) before computing the handle offset
     *  against the source's segment, then the mirror re-derives via
     *  `rederiveConnectionsAffectedBy`. */
    bidirectionalMirrorMatrices?: MirrorMatrix[];
  } | null = null;

  // Curve-bend drag state — set in "edit" sub-mode when the user
  // clicks on a construction line's stroke (not a handle). All
  // positional info is stored by *index* (not by paper.js object
  // reference) because `setSelection` triggers a Scene re-render that
  // destroys the original segments — direct references would point at
  // detached objects after the first event. We re-resolve the live
  // segments via the indices each frame, same trick the vertex drag
  // uses.
  private curveDrag: {
    constructionId: string;
    pathIndex: number;
    /** Index of the curve within the sub-path. `subpath.curves[curveIndex]`
     *  gives the live Curve, whose `segment1.handleOut` /
     *  `segment2.handleIn` are the two values we mutate to bend the line. */
    curveIndex: number;
    /** Bezier parameter (0..1) on the curve where the click landed.
     *  Clamped away from the endpoints when used so 1/(t(1-t)) stays
     *  finite (clicks near a vertex would otherwise blow up the
     *  required handle magnitudes). */
    t: number;
    /** Handle values at the moment of click. Stored as plain {x,y}
     *  (not paper.Point) so they survive across re-renders.
     *  For bidirectional mirror drags these are the SOURCE-line's
     *  handles (which is the curve we actually mutate). */
    origHandleOut: { x: number; y: number };
    origHandleIn: { x: number; y: number };
    /** Cursor position at click time. The drag is interpreted as the
     *  difference between the current cursor and this anchor.
     *  For bidirectional mirror drags `downPt` is the cursor mapped
     *  through the chain into SOURCE space, so the per-frame
     *  `cursorSource - downPt` delta lands in the right frame. */
    downPt: { x: number; y: number };
    /** Bidirectional editing (issue #180): when the gesture started
     *  on a mirror-derived line, `constructionId` was swapped to
     *  the source root. Per-frame the cursor is mapped through this
     *  chain (outer-to-inner) before computing the bend delta. */
    bidirectionalMirrorMatrices?: MirrorMatrix[];
  } | null = null;

  constructor(private host: VertexEditHost) {}

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** True while any of the three vertex-edit drags is in flight. */
  hasActiveDrag(): boolean {
    return !!(this.vertexDrag || this.curveDrag || this.bezierHandleDrag);
  }

  /** Drop every in-flight vertex-edit drag. Called from the tool's
   *  sub-mode / picker switches, onActivate/onDeactivate, onCancel and
   *  cancelDraft so a half-finished reshape doesn't bleed across. */
  reset(): void {
    this.vertexDrag = null;
    this.curveDrag = null;
    this.bezierHandleDrag = null;
  }

  /** onDown delegation for the Edit sub-mode handle / anchor / curve
   *  starts. Returns true when the press was consumed (a drag was
   *  seeded, a mirror gesture refused, or the smooth-toggle pending
   *  tap captured). Preserves the exact original in-order dispatch:
   *  bezier tip → anchor → curve stroke. */
  maybeStartDrag(
    p: { x: number; y: number },
    pt: paper.Point,
    handle: HandleHit | null,
    isSmoothToggle: boolean,
  ): boolean {
    if (handle?.kind === "bezier-tip") {
      // Issue #180 bidirectional editing: handle drag on a mirror-
      // derived line routes the chain back to the source root the
      // same way the vertex / line-drag flows do. Per-frame the
      // cursor maps through the matrices into source space before
      // the handle offset is recomputed against the source's
      // segment; the mirror re-derives from the live source via
      // `rederiveConnectionsAffectedBy`. Offset-derived stays
      // refused (no clean inverse).
      const hitLine = projectConstructionLines(this.host.getProject()).find(
        (l) => l.id === handle.constructionId,
      );
      let handleConstructionId = handle.constructionId;
      let bidirectionalMirrorMatrices: MirrorMatrix[] | undefined;
      if (hitLine && isMirrorDerived(hitLine)) {
        const route = this.host.tryRouteMirrorLineDragToSource(
          handle.constructionId,
        );
        if (!route) return true;
        handleConstructionId = route.sourceRootId;
        bidirectionalMirrorMatrices = route.matrices;
      } else if (this.host.isDerivedLineId(handle.constructionId)) {
        return true;
      }
      // Tangent-handle endpoint — drag adjusts a single side of the
      // segment's bezier (handleIn xor handleOut), not the anchor
      // itself. Works in both edit modes; smooth-toggle still
      // wants tangent adjustment to "just work".
      this.bezierHandleDrag = {
        constructionId: handleConstructionId,
        pathIndex: handle.pathIndex,
        segmentIndex: handle.segmentIndex,
        side: handle.side,
        ...(bidirectionalMirrorMatrices ? { bidirectionalMirrorMatrices } : {}),
      };
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        constructionId: handle.constructionId,
      });
      return true;
    }
    if (handle?.kind === "anchor") {
      // Issue #180 bidirectional vertex editing: a vertex grab on a
      // mirror-derived line routes through `tryRouteMirrorLineDragToSource`
      // — the drag target becomes the SOURCE root, and per-frame
      // cursor positions get fed through the chain of axis matrices
      // to compute the source-space anchor target. Offset-derived
      // lines stay refused (no clean inverse for perpendicular-
      // distance offset math). The same anchor `(pathIndex,
      // segmentIndex)` flows through unchanged because paper.js's
      // `CompoundPath.transform()` preserves segment order on the
      // simple paths the mirror deriver produces.
      const hitLine = projectConstructionLines(this.host.getProject()).find(
        (l) => l.id === handle.constructionId,
      );
      let vertexConstructionId = handle.constructionId;
      let bidirectionalMirrorMatrices: MirrorMatrix[] | undefined;
      if (hitLine && isMirrorDerived(hitLine)) {
        const route = this.host.tryRouteMirrorLineDragToSource(
          handle.constructionId,
        );
        if (!route) {
          // Mirror-of-offset or broken chain — refuse with a brief
          // hint so the silent early-return doesn't read as a freeze.
          showCanvasToast(MIRROR_OFFSET_REFUSE_HINT);
          return true;
        }
        vertexConstructionId = route.sourceRootId;
        bidirectionalMirrorMatrices = route.matrices;
      } else if (this.host.isDerivedLineId(handle.constructionId)) {
        // Offset-derived (no inverse) — refuse outright.
        return true;
      }
      if (isSmoothToggle) {
        // Anchor press in smooth-toggle: don't start a vertex
        // drag (node movement is intentionally disabled here).
        // Capture as a pending tap; if the user releases without
        // dragging past the slop, `onUp` flips its smooth-lock.
        this.host.setSmoothTogglePending({ x: p.x, y: p.y });
        return true;
      }
      // Capture the anchor's pre-drag project position so the
      // per-frame chain pass can compute a frame-over-frame delta
      // from the live anchor's current position. For bidirectional
      // editing the anchor lives on the SOURCE line — read from
      // there directly.
      const seg = this.findRenderedSegment({
        constructionId: vertexConstructionId,
        pathIndex: handle.pathIndex,
        segmentIndex: handle.segmentIndex,
      });
      const startAnchorPos = seg
        ? { x: seg.point.x, y: seg.point.y }
        : { x: p.x, y: p.y };
      const draggedAnchorRef: AnchorRefLite = {
        kind: "anchor",
        constructionId: vertexConstructionId,
        pathIndex: handle.pathIndex,
        segmentIndex: handle.segmentIndex,
      };
      // Pre-compute the rigid-translation closure (snap-chain
      // partners of the dragged anchor and their offset
      // descendants) and the origin-anchor pulls (anchors on the
      // dragged line snap-pinned to closure lines) once at
      // mouseDown — they don't change mid-drag and the per-frame
      // loop is then a fixed-cost translate pass instead of a
      // graph re-walk.
      const closure = computeVertexDragClosure(
        this.host.getProject(),
        draggedAnchorRef,
      );
      const originAnchorPulls = originAnchorPullsForVertexDrag(
        this.host.getProject(),
        draggedAnchorRef,
        closure,
      );
      this.vertexDrag = {
        constructionId: vertexConstructionId,
        pathIndex: handle.pathIndex,
        segmentIndex: handle.segmentIndex,
        startAnchorPos,
        lastAnchorPos: startAnchorPos,
        closure,
        originAnchorPulls,
        ...(bidirectionalMirrorMatrices ? { bidirectionalMirrorMatrices } : {}),
      };
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        constructionId: handle.constructionId,
      });
      return true;
    }
    // Stroke hit on a construction line → start a curve-bend drag
    // on whichever curve segment the cursor landed on. Available
    // in both edit modes so the user can grab and bend curves
    // even when smooth-toggle is the active interaction. The
    // visible anchor + bezier-tip circles are already claimed by
    // the unified `findVisibleHandleHit` branches above, so taps
    // inside any handle dot are captured there — no separate
    // anchor-priority guard needed here.
    const curveHit = this.host.hitConstructionCurve(pt);
    if (curveHit) {
      // Issue #180 bidirectional editing: a curve bend on a
      // mirror-derived line routes through the source root the
      // same way the vertex / line-drag / handle flows do. The
      // curve at the same `(pathIndex, curveIndex)` exists on the
      // source line (paper.js's `CompoundPath.transform()`
      // preserves curve order on the simple shapes the mirror
      // deriver produces); we snapshot the source's bracketing
      // handles and store `downPt` in source space so the per-
      // frame delta lands in the right frame. Mirror re-derives
      // every frame via `rederiveConnectionsAffectedBy`. Offset-
      // derived stays refused (no clean inverse).
      const curveHitLine = projectConstructionLines(
        this.host.getProject(),
      ).find((l) => l.id === curveHit.constructionId);
      let curveConstructionId = curveHit.constructionId;
      let bidirectionalMirrorMatrices: MirrorMatrix[] | undefined;
      let startHandleSnap = {
        x: curveHit.curve.segment1.handleOut.x,
        y: curveHit.curve.segment1.handleOut.y,
      };
      let endHandleSnap = {
        x: curveHit.curve.segment2.handleIn.x,
        y: curveHit.curve.segment2.handleIn.y,
      };
      let downPt = { x: p.x, y: p.y };
      if (curveHitLine && isMirrorDerived(curveHitLine)) {
        const route = this.host.tryRouteMirrorLineDragToSource(
          curveHit.constructionId,
        );
        if (!route) return true;
        curveConstructionId = route.sourceRootId;
        bidirectionalMirrorMatrices = route.matrices;
        // Snapshot the SOURCE's bracketing handles — that's the
        // curve the per-frame mutation operates on. Source curve
        // sits at the same (pathIndex, curveIndex) thanks to the
        // segment-order-preserving transform paper.js does on
        // simple paths.
        const sourceCurve = this.findRenderedCurve({
          constructionId: curveConstructionId,
          pathIndex: curveHit.pathIndex,
          curveIndex: curveHit.curveIndex,
        });
        if (!sourceCurve) return true;
        startHandleSnap = {
          x: sourceCurve.segment1.handleOut.x,
          y: sourceCurve.segment1.handleOut.y,
        };
        endHandleSnap = {
          x: sourceCurve.segment2.handleIn.x,
          y: sourceCurve.segment2.handleIn.y,
        };
        // Map the click point to source space so per-frame deltas
        // (sourceCursor - downPt) land in source space.
        let mapped = { x: p.x, y: p.y };
        for (const m of route.matrices) {
          mapped = applyMirror(m, mapped);
        }
        downPt = mapped;
      } else if (this.host.isDerivedLineId(curveHit.constructionId)) {
        return true;
      }
      this.curveDrag = {
        constructionId: curveConstructionId,
        pathIndex: curveHit.pathIndex,
        curveIndex: curveHit.curveIndex,
        t: curveHit.t,
        origHandleOut: startHandleSnap,
        origHandleIn: endHandleSnap,
        downPt,
        ...(bidirectionalMirrorMatrices ? { bidirectionalMirrorMatrices } : {}),
      };
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        constructionId: curveHit.constructionId,
      });
      setCanvasCursor(this.host.getScope(), "grabbing");
      return true;
    }
    return false;
  }

  /** onMove delegation: advance whichever vertex-edit drag is live.
   *  Returns true when one consumed the move. Mirrors the original
   *  in-order dispatch (vertex → bezier handle → curve). */
  handleMove(s: PointerSample, p: { x: number; y: number }): boolean {
    if (this.vertexDrag) {
      const scope = this.host.getScope();
      const seg = this.findRenderedSegment(this.vertexDrag);
      if (seg) {
        // Issue #180 bidirectional vertex editing: when the cursor
        // is in MIRROR space (the user grabbed a vertex on a mirror-
        // derived line, onDown re-routed to the source root), map
        // the cursor through each axis matrix in chain order to
        // recover the SOURCE-space target. Snap is intentionally
        // skipped for bidirectional drags in V1 — candidates would
        // need to be evaluated in mirror space, deferred to a follow-
        // up. For non-bidirectional drags the existing snap path
        // runs unchanged.
        let targetPos: { x: number; y: number };
        if (this.vertexDrag.bidirectionalMirrorMatrices) {
          let mapped = { x: p.x, y: p.y };
          for (const m of this.vertexDrag.bidirectionalMirrorMatrices) {
            mapped = applyMirror(m, mapped);
          }
          targetPos = mapped;
        } else {
          // Snap-during-drag: when the cursor is within the snap
          // radius of another line's anchor, jump the dragged point
          // onto that anchor; releasing the drag while snapped pins
          // them with a coincident constraint (see onUp). Excluded
          // lines for the snap-candidate search are the pre-computed
          // drag closure plus the dragged line itself — those lines
          // are all moving (or being edited), so their pre-drag
          // positions would otherwise surface as stale ghost
          // candidates (#102, extended for #29).
          // Closing a loop is the most specific intent: if the dragged
          // endpoint nears its subpath's OTHER endpoint, jump it exactly onto
          // it so the shape snaps shut visibly as you drag (#684); the release
          // then closes the loop.
          const closeTarget = this.ownLoopCloseTarget(this.vertexDrag, {
            x: p.x,
            y: p.y,
          });
          if (closeTarget) {
            targetPos = closeTarget;
            setAngleHint(null);
            this.host.clearLiveSnap();
          } else {
            const ghostExcluded = new Set<string>(this.vertexDrag.closure);
            ghostExcluded.add(this.vertexDrag.constructionId);
            const snap = this.host.updateLiveSnap(
              [
                {
                  ref: {
                    kind: "anchor",
                    constructionId: this.vertexDrag.constructionId,
                    pathIndex: this.vertexDrag.pathIndex,
                    segmentIndex: this.vertexDrag.segmentIndex,
                  },
                  pos: { x: p.x, y: p.y },
                },
              ],
              ghostExcluded,
            );
            if (snap) {
              // Point-snap won — landing on another line's anchor is a
              // more specific intent than grid / angle alignment, so it
              // takes priority and clears any lingering angle hint.
              targetPos = snap.candidatePos;
              setAngleHint(null);
            } else {
              // No point-snap fired this frame. Honour the Grid / Angle
              // toggles for the edited vertex (#298) so point editing
              // aligns the dragged point the same way drawing does.
              targetPos = this.snapEditedVertex(seg, p, angleModifierHeld(s));
            }
          }
        }
        seg.point = new scope.Point(targetPos.x, targetPos.y);
        // Rebuild the handle indicators from the live (just-mutated)
        // path so the bezier tangent lines + tip dots travel with the
        // anchor instead of staying stuck at the old absolute
        // position. This also keeps the anchor's own dot in sync.
        this.host
          .getScene()
          .refreshConstructionHandles(this.vertexDrag.constructionId);
        // Re-paint the dark halo from the (just-mutated) main path so
        // it tracks the distorted geometry instead of lagging at the
        // pre-drag shape.
        this.host
          .getScene()
          .refreshConstructionLineHalo(this.vertexDrag.constructionId);
        const frameDx = targetPos.x - this.vertexDrag.lastAnchorPos.x;
        const frameDy = targetPos.y - this.vertexDrag.lastAnchorPos.y;
        // Pull non-dragged anchors on the dragged line that are
        // snap-pinned to anchors on closure lines — keeps the
        // coincident pair satisfied as the dragged line distorts.
        // Pre-computed at mouseDown; applied here every frame.
        this.applyVertexDragOriginPulls(
          this.vertexDrag.constructionId,
          this.vertexDrag.originAnchorPulls,
          frameDx,
          frameDy,
        );
        // Vertex drag distorts the source's geometry (one anchor
        // moved + any pulled anchors), so every transitive
        // dependent (offset descendants, mirror dependents, etc.)
        // re-derives from the source's NEW pathData each frame
        // via the Connections registry. A plain item-level
        // translate isn't enough — the dependent paths' shapes
        // would lag.
        this.host
          .getScene()
          .rederiveConnectionsAffectedBy(this.vertexDrag.constructionId);
        // Rigidly translate every closure line (snap-chain partners
        // of the dragged anchor + their offset descendants). The
        // dragged line and its own offsets are NOT in the closure
        // by construction (handled by the segment edit +
        // `rederiveOffsetsOf` above).
        this.host.translateDragClosure(
          this.vertexDrag.closure,
          new Set(),
          frameDx,
          frameDy,
        );
        this.vertexDrag.lastAnchorPos = { x: targetPos.x, y: targetPos.y };
        // Snap-candidate rings render from live segment positions —
        // refresh after the closure pass so they land on post-chain
        // anchor positions, not stale pre-frame spots.
        this.host.refreshLiveSnapOverlay();
        // Per-line panel live mirror — when the dragged line is
        // currently a Line primitive (the most common case after
        // canonicalization), push the live length + visual angle
        // into the per-line panel's inputs each frame. Reads the
        // rendered item's segments so the values reflect the in-
        // progress geometry, not the pre-drag model snapshot.
        this.host.notifyLinePrimitiveLiveUpdate(
          this.vertexDrag.constructionId,
          scope,
        );
        // #746: rays for the point move — the move vector (distance + angle),
        // plus, for each straight segment the point connects, the segment's new
        // angle vs horizontal and the rotation from its old angle.
        const moveRays = this.buildVertexMoveRays(
          seg,
          this.vertexDrag.startAnchorPos,
          targetPos,
        );
        this.host.getScene().setGuideRays(moveRays.length ? moveRays : null);
      }
      return true;
    }
    if (this.bezierHandleDrag) {
      const scope = this.host.getScope();
      const drag = this.bezierHandleDrag;
      const seg = this.findRenderedSegment(drag);
      if (seg) {
        // Bidirectional mirror route: map the cursor through the
        // chain into source space before computing the handle offset
        // against the source's segment. Mirror chain matrices are
        // self-inverse reflections, applied outer-to-inner — same
        // mapping the vertex-drag uses.
        let cursor = { x: p.x, y: p.y };
        if (drag.bidirectionalMirrorMatrices) {
          for (const m of drag.bidirectionalMirrorMatrices) {
            cursor = applyMirror(m, cursor);
          }
        }
        // handleIn / handleOut are stored as offsets from seg.point,
        // so the new offset is just `cursor - anchor`.
        const offset = new scope.Point(
          cursor.x - seg.point.x,
          cursor.y - seg.point.y,
        );
        if (drag.side === "in") seg.handleIn = offset;
        else seg.handleOut = offset;
        // Locked anchor → constrain the OPPOSITE handle so the
        // user's lock (angle-only or full smooth mirror) stays
        // satisfied during the drag. Independent anchors (mode
        // null) leave the opposite side alone.
        const lockMode = this.getAnchorLockMode(drag);
        const mirrored = this.mirroredHandleFor(seg, drag.side, lockMode);
        if (mirrored) {
          if (drag.side === "in") seg.handleOut = mirrored;
          else seg.handleIn = mirrored;
        }
        this.host.getScene().refreshConstructionHandles(drag.constructionId);
        // Halo tracks the bent curve too — same lag class as the
        // vertex-drag refresh above.
        this.host.getScene().refreshConstructionLineHalo(drag.constructionId);
        // Bezier-handle drag reshapes the source curve, so every
        // dependent (offsets, mirrors, …) re-derives from the new
        // pathData each frame via the Connections registry.
        this.host.getScene().rederiveConnectionsAffectedBy(drag.constructionId);
      }
      return true;
    }
    if (this.curveDrag) {
      // Re-bend the bezier so it passes through the current cursor.
      // For a cubic bezier B(t), perturbing both bracketing handles by
      // the same vector h shifts B(t) by 3·t·(1-t)·h. Solve for h to
      // hit the cursor delta, then add it on top of the original
      // handles snapshot. Clamp t away from 0 / 1 so handles don't
      // explode when the click landed near a vertex.
      const scope = this.host.getScope();
      const drag = this.curveDrag;
      const curve = this.findRenderedCurve(drag);
      if (curve) {
        // Bidirectional mirror route: map the cursor through the
        // chain into source space, then take the delta against the
        // SOURCE-space `downPt`. This lands the handle adjustment on
        // the source curve, which the mirror re-derives off of.
        let cursor = { x: p.x, y: p.y };
        if (drag.bidirectionalMirrorMatrices) {
          for (const m of drag.bidirectionalMirrorMatrices) {
            cursor = applyMirror(m, cursor);
          }
        }
        // Straight-first-bend arc + shared-delta re-bend live in the
        // shared geometry module (#969) so the piece outline editor
        // bends curves with the exact same math.
        applyCurveBend(
          scope,
          curve,
          {
            t: drag.t,
            origHandleOut: drag.origHandleOut,
            origHandleIn: drag.origHandleIn,
            downPt: drag.downPt,
          },
          cursor,
        );
        // If either bracketing anchor is locked, constrain the
        // OUTSIDE handle of that anchor so the lock (angle-only or
        // full smooth mirror) stays satisfied while the curve
        // bends. Without this, bending a curve attached to a
        // locked anchor would leave the outside tangent stuck at
        // its old angle and visibly break the constraint.
        const seg1Lock = this.getAnchorLockMode({
          constructionId: drag.constructionId,
          pathIndex: drag.pathIndex,
          segmentIndex: curve.segment1.index,
        });
        const seg1Mirror = this.mirroredHandleFor(
          curve.segment1,
          "out",
          seg1Lock,
        );
        if (seg1Mirror) curve.segment1.handleIn = seg1Mirror;
        const seg2Lock = this.getAnchorLockMode({
          constructionId: drag.constructionId,
          pathIndex: drag.pathIndex,
          segmentIndex: curve.segment2.index,
        });
        const seg2Mirror = this.mirroredHandleFor(
          curve.segment2,
          "in",
          seg2Lock,
        );
        if (seg2Mirror) curve.segment2.handleOut = seg2Mirror;
        // Rebuild the bezier-handle indicators from the live (just-
        // mutated) path so the lines + hollow tips track the bend
        // in real time, not just on commit.
        this.host.getScene().refreshConstructionHandles(drag.constructionId);
        // Same lag fix for the dark halo — re-paint from the bent
        // pathData so it tracks the curve in lock-step.
        this.host.getScene().refreshConstructionLineHalo(drag.constructionId);
        // Curve-bend reshapes the source path; every dependent
        // (offsets, mirrors, …) re-derives from the new geometry
        // each frame via the Connections registry.
        this.host.getScene().rederiveConnectionsAffectedBy(drag.constructionId);
      }
      return true;
    }
    return false;
  }

  /** onUp delegation: commit whichever vertex-edit drag is live and
   *  clear its state. Returns true when one consumed the release.
   *  Mirrors the original in-order dispatch (vertex → bezier handle →
   *  curve); the three states are mutually exclusive (a single onDown
   *  seeds exactly one). */
  handleUp(p: { x: number; y: number }): boolean {
    if (this.vertexDrag) {
      const drag = this.vertexDrag;
      this.vertexDrag = null;
      // Clear any angle hint left over from an angle-snapped vertex
      // edit (#298) — the drag is finishing, so the hint shouldn't
      // linger on the canvas.
      setAngleHint(null);
      // Capture the snap candidate (if any) before we null out
      // `liveSnap`, so the commit reducer can append a coincident
      // Constraint at release time. The visible drag has already
      // jumped the anchor onto the candidate position, so the
      // commit's pathData reflects the snapped state — appending
      // the constraint just records the relationship.
      const snapAtRelease = this.host.getLiveSnap();
      this.host.clearLiveSnap();
      const item = this.host
        .getScene()
        .getConstructionLineItem(drag.constructionId);
      if (item) {
        // Read the current pathData off the live (mutated) item — that
        // already reflects the dragged anchor's new position — and
        // commit it as the new line state. If an endpoint was dragged onto
        // the subpath's other endpoint, close the loop first (#684).
        const newPathData = this.closeDraggedLoopIfSnapped(
          item as paper.PathItem,
          drag,
        );
        // Vertex drag warps the geometry away from any parametric
        // primitive — drop the metadata so the line stops calling
        // itself a "Square" / "Ellipse" / etc. and the panel hides
        // the dimension inputs.
        const scope = this.host.getScope();
        this.host.commit("Edit construction line", (proj) => {
          const linesAfter = (proj.constructionLines ?? []).map((l) =>
            l.id === drag.constructionId
              ? dropConstructionPrimitive(
                  l,
                  proj,
                  { pathData: newPathData },
                  { scope },
                )
              : l,
          );
          // Drop any center-ref coincident constraints whose target
          // line just lost its primitive (vertex drag can distort a
          // rectangle into a free polygon). The bbox-centre snap is
          // only meaningful while the partner still has a primitive,
          // so an orphaned center ref must be deleted, not kept.
          const prunedConstraints = pruneInvalidCenterConstraints(
            linesAfter,
            proj.constraints,
          );
          const projAfterPrune =
            prunedConstraints === proj.constraints
              ? proj
              : { ...proj, constraints: prunedConstraints };
          // Snap (coincident) enforcement: rigidly translate every
          // line in the pre-computed drag closure by the dragged
          // anchor's total displacement. Mirrors the live-drag
          // closure translation — partners and their offset
          // descendants stay in lock-step. The dragged line is NOT
          // in the closure (its geometry was directly edited and
          // `linesAfter` already reflects the new pathData plus any
          // origin-anchor pulls applied during onMove).
          //
          // Append a new coincident constraint if the drag ended
          // inside the snap radius of another line's anchor. Dedupes
          // against any existing pair so re-snapping to a partner
          // you're already pinned to is a no-op rather than a
          // stacking of duplicate constraints.
          const projWithSnap =
            snapAtRelease &&
            snapAtRelease.source.kind === "anchor" &&
            isPointConstraintActive(this.host.getSettings()) &&
            isPinnableSnapRef(snapAtRelease.candidate) &&
            !hasCoincidentConstraint(
              projAfterPrune,
              snapAtRelease.source,
              snapAtRelease.candidate,
            )
              ? {
                  ...projAfterPrune,
                  constraints: [
                    ...(projAfterPrune.constraints ?? []),
                    {
                      id: newId(),
                      kind: "coincident" as const,
                      refs: [
                        {
                          kind: "anchor" as const,
                          constructionId: snapAtRelease.source.constructionId,
                          pathIndex: snapAtRelease.source.pathIndex,
                          segmentIndex: snapAtRelease.source.segmentIndex,
                        },
                        snapRefToConstraintRef(snapAtRelease.candidate),
                      ],
                    },
                  ],
                }
              : projAfterPrune;
          // Translate the closure (snap-chain partners + offset
          // descendants reachable from any of them) by the dragged
          // anchor's total displacement. The dragged line is NOT
          // in the closure — its geometry was directly edited in
          // `linesAfter` and any origin-anchor pulls were applied
          // during onMove.
          const totalDx = drag.lastAnchorPos.x - drag.startAnchorPos.x;
          const totalDy = drag.lastAnchorPos.y - drag.startAnchorPos.y;
          const withTranslatedClosure = translateClosureInProject(
            { ...projWithSnap, constructionLines: linesAfter },
            drag.closure,
            new Set([drag.constructionId]),
            totalDx,
            totalDy,
            scope,
          );
          // Final alignment pass — same role as the line/group
          // drag commit: a no-op for satisfied constraints, snaps
          // partners onto the dragged anchor for any unsatisfied
          // ones. Also handles chain-through-origin pulls beyond
          // the pre-computed `originAnchorPulls` set (those were
          // applied during onMove; this catches anything reached
          // by the post-drag BFS that wasn't reachable at
          // mouseDown).
          const enforced = propagateCoincidentAfterAnchorDrag(
            withTranslatedClosure,
            withTranslatedClosure.constructionLines ?? [],
            {
              constructionId: drag.constructionId,
              pathIndex: drag.pathIndex,
              segmentIndex: drag.segmentIndex,
            },
            scope,
          );
          // A partner linked at two points can now be stretched by the
          // chain (#1166); keep its primitive honest.
          return settleSnapReshapedLines(
            withTranslatedClosure,
            withTranslatedClosure.constructionLines ?? [],
            enforced,
            scope,
          );
        });
      }
      return true;
    }
    if (this.bezierHandleDrag) {
      const drag = this.bezierHandleDrag;
      this.bezierHandleDrag = null;
      const item = this.host
        .getScene()
        .getConstructionLineItem(drag.constructionId);
      if (item) {
        const newPathData = (item as paper.PathItem).pathData;
        const scope = this.host.getScope();
        this.host.commit("Adjust bezier handle", (proj) => {
          const linesAfter = (proj.constructionLines ?? []).map((l) =>
            l.id === drag.constructionId
              ? dropConstructionPrimitive(
                  l,
                  proj,
                  { pathData: newPathData },
                  { scope },
                )
              : l,
          );
          return {
            ...proj,
            constructionLines: linesAfter,
            constraints: pruneInvalidCenterConstraints(
              linesAfter,
              proj.constraints,
            ),
          };
        });
      }
      this.host.refreshEditCursor(p);
      return true;
    }
    if (this.curveDrag) {
      const drag = this.curveDrag;
      this.curveDrag = null;
      const item = this.host
        .getScene()
        .getConstructionLineItem(drag.constructionId);
      if (item) {
        // Read the live (already-mutated) pathData and commit it as
        // the line's new shape — same trick as the vertex-drag commit.
        const newPathData = (item as paper.PathItem).pathData;
        const scope = this.host.getScope();
        this.host.commit("Bend construction line", (proj) => {
          const linesAfter = (proj.constructionLines ?? []).map((l) =>
            l.id === drag.constructionId
              ? dropConstructionPrimitive(
                  l,
                  proj,
                  { pathData: newPathData },
                  { scope },
                )
              : l,
          );
          return {
            ...proj,
            constructionLines: linesAfter,
            constraints: pruneInvalidCenterConstraints(
              linesAfter,
              proj.constraints,
            ),
          };
        });
      }
      // Drag is over — drop the "grabbing" cursor immediately. If the
      // pointer is still over a line / handle the next onHover will
      // upgrade it back to "grab" / "move"; otherwise it stays at
      // "default". Without this the cursor sticks at "grabbing" until
      // the next pointer move.
      this.host.refreshEditCursor(p);
      return true;
    }
    return false;
  }

  // -------------------------------------------------------------------
  // Drag-exclusive geometry helpers (moved with the drags)
  // -------------------------------------------------------------------

  /** Re-resolve the live curve being bent from its indices. The
   *  Scene re-renders on every selection / commit, so any direct
   *  segment / curve references captured at click time become
   *  detached after the first event — looking them up by index is
   *  what keeps the drag visually alive. */
  private findRenderedCurve(drag: {
    constructionId: string;
    pathIndex: number;
    curveIndex: number;
  }): paper.Curve | null {
    const item = this.host
      .getScene()
      .getConstructionLineItem(drag.constructionId);
    if (!item) return null;
    const compound = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      compound.children && compound.children.length > 0
        ? (compound.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[drag.pathIndex];
    return sub?.curves[drag.curveIndex] ?? null;
  }

  /** Apply the Grid / Angle snap modifiers to a vertex being edited
   *  in the Edit sub-mode (#298), returning the adjusted target
   *  position for the dragged anchor `seg`. Callers handle the
   *  higher-priority point-to-anchor snap first; this runs only when
   *  none fired.
   *
   *  Precedence mirrors the Draw flow: angle snap wins, constraining
   *  the edited segment to the nearest common drafting angle measured
   *  from the neighbouring anchor (the previous segment's point, or
   *  the next one for the first anchor of an open path). Grid snap is
   *  the lowest-priority fallback, nudging the point to the nearest
   *  cutting-mat lattice intersection only when angle snap didn't
   *  move it. Updates the on-canvas angle hint as a side effect. */
  private snapEditedVertex(
    seg: paper.Segment,
    p: { x: number; y: number },
    shiftKey: boolean,
  ): { x: number; y: number } {
    const neighborSeg = seg.previous ?? seg.next;
    const neighbor = neighborSeg
      ? { x: neighborSeg.point.x, y: neighborSeg.point.y }
      : null;
    if (neighbor && isAngleSnapActive(this.host.getSettings(), shiftKey)) {
      const sn = snapToCommonAngle(neighbor, p);
      setAngleHint(sn.angleRad);
      return { x: sn.x, y: sn.y };
    }
    setAngleHint(null);
    // Line snap in edit mode too — land the dragged vertex ON a nearby
    // construction edge, exactly like drawing (#323), excluding the line being
    // edited so it never snaps to itself. Wins over grid (land-on-edge beats the
    // lattice); the anchor / self-close snaps resolved by the caller still win
    // over it. Requested follow-up to #658 — a snapped end then reads as a square.
    const exclude = this.vertexDrag?.constructionId;
    // A crossing of two OTHER lines (#128) beats a point along one of them.
    const cross = this.host.findIntersectionSnap(p, exclude);
    if (cross) return cross;
    const onLine = this.host.findLineSnap(p, exclude);
    if (onLine) return onLine;
    if (isGridSnapActive(this.host.getSettings())) {
      const grid = this.host.getScene().getMatGridInfo();
      if (grid) return snapPointToGrid(p, grid);
    }
    return { x: p.x, y: p.y };
  }

  /** The position to snap a dragged endpoint onto so a loop closes as you drag
   *  (#684): its subpath's OTHER endpoint, when the cursor is within the snap
   *  radius of it and the dragged vertex is an endpoint of an open subpath. Null
   *  otherwise. Reading the live item is safe — the other endpoint isn't the one
   *  being dragged, so its position is stable this frame. */
  private ownLoopCloseTarget(
    drag: { constructionId: string; pathIndex: number; segmentIndex: number },
    cursor: { x: number; y: number },
  ): { x: number; y: number } | null {
    const item = this.host
      .getScene()
      .getConstructionLineItem(drag.constructionId);
    if (!item) return null;
    const compound = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      compound.children && compound.children.length > 0
        ? (compound.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[drag.pathIndex];
    if (!sub || sub.closed) return null;
    const n = sub.segments.length;
    if (n < 3) return null;
    const isEndpoint = drag.segmentIndex === 0 || drag.segmentIndex === n - 1;
    if (!isEndpoint) return null;
    const otherIndex = drag.segmentIndex === 0 ? n - 1 : 0;
    const other = sub.segments[otherIndex]!.point;
    const scope = this.host.getScope();
    const d = new scope.Point(cursor.x, cursor.y).getDistance(other);
    if (d > closeSnapRadius(scope.view?.zoom ?? 1)) return null;
    return { x: other.x, y: other.y };
  }

  /** When the just-dragged vertex is an ENDPOINT of an OPEN subpath and it landed
   *  within `CLOSE_SNAP_RADIUS` of that subpath's OTHER endpoint, close the
   *  subpath into a loop (#684) so the following commit reads closed pathData.
   *  Keeps every vertex (no removal / merge), so the downstream closure +
   *  constraint passes still see valid segment indices. Returns the pathData to
   *  commit — closed when it snapped shut, else the item's current (open) form. */
  private closeDraggedLoopIfSnapped(
    item: paper.PathItem,
    drag: { pathIndex: number; segmentIndex: number },
  ): string {
    const compound = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      compound.children && compound.children.length > 0
        ? (compound.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[drag.pathIndex];
    if (!sub || sub.closed) return item.pathData;
    const n = sub.segments.length;
    if (n < 3) return item.pathData; // need ≥ 3 vertices to enclose an area
    const isEndpoint = drag.segmentIndex === 0 || drag.segmentIndex === n - 1;
    if (!isEndpoint) return item.pathData;
    const otherIndex = drag.segmentIndex === 0 ? n - 1 : 0;
    const dragged = sub.segments[drag.segmentIndex]!.point;
    const other = sub.segments[otherIndex]!.point;
    const closeR = closeSnapRadius(this.host.getScope().view?.zoom ?? 1);
    if (dragged.getDistance(other) > closeR) return item.pathData;
    sub.closed = true;
    return item.pathData;
  }

  /** Walk the live scene tree to find the paper.js Segment a vertex
   *  drag is mutating. Returns null if the rendered item disappeared
   *  (project re-rendered between events). */
  private findRenderedSegment(drag: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
  }): paper.Segment | null {
    const item = this.host
      .getScene()
      .getConstructionLineItem(drag.constructionId);
    if (!item) return null;
    const compound = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      compound.children && compound.children.length > 0
        ? (compound.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[drag.pathIndex];
    return sub?.segments[drag.segmentIndex] ?? null;
  }

  /** Translate the pre-computed origin-anchor pulls for a vertex
   *  drag: anchors on the dragged line OTHER than the dragged anchor
   *  that are snap-pinned to anchors on closure-lines. Each gets
   *  translated by the frame delta so the coincident pair stays
   *  satisfied — the dragged line distorts rather than the
   *  constraint visibly breaking.
   *
   *  After pulling, the dragged line's offsets re-derive from its
   *  freshly distorted pathData via `rederiveOffsetsOf`. The caller
   *  is responsible for that call (it's already part of the regular
   *  vertex-drag onMove flow). */
  private applyVertexDragOriginPulls(
    draggedLineId: string,
    pulls: ReadonlyArray<{ pathIndex: number; segmentIndex: number }>,
    dx: number,
    dy: number,
  ): void {
    if (pulls.length === 0) return;
    if (dx === 0 && dy === 0) return;
    const scope = this.host.getScope();
    const item = this.host.getScene().getConstructionLineItem(draggedLineId);
    if (!item) return;
    const cp = item as paper.CompoundPath;
    const subs: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [item as unknown as paper.Path];
    for (const pull of pulls) {
      const sub = subs[pull.pathIndex];
      if (!sub) continue;
      const seg = sub.segments[pull.segmentIndex];
      if (!seg) continue;
      seg.point = new scope.Point(seg.point.x + dx, seg.point.y + dy);
    }
    this.host.getScene().refreshConstructionHandles(draggedLineId);
  }

  /** Build the point-move rays (#746): the move vector (distance + angle), plus
   *  — for each STRAIGHT segment the vertex connects — a ray at the neighbour
   *  showing that segment's new angle vs the horizontal and the interior arc
   *  from its old angle (how far the segment rotated). Curved (bezier) segments
   *  are skipped. */
  private buildVertexMoveRays(
    seg: paper.Segment,
    oldPos: { x: number; y: number },
    newPos: { x: number; y: number },
  ): GuideRay[] {
    const rays: GuideRay[] = [];
    const mdx = newPos.x - oldPos.x;
    const mdy = newPos.y - oldPos.y;
    const mlen = Math.hypot(mdx, mdy);
    if (mlen < 1e-6) return rays;
    let mdeg = (Math.atan2(-mdy, mdx) * 180) / Math.PI;
    if (mdeg < 0) mdeg += 360;
    rays.push({
      from: { x: oldPos.x, y: oldPos.y },
      to: { x: newPos.x, y: newPos.y },
      lengthMm: mlen,
      angleDeg: mdeg,
    });

    const path = seg.path as paper.Path | undefined;
    if (!path || !path.curves || path.curves.length === 0) return rays;
    const curves = path.curves;
    const idx = seg.index;
    // Curve INTO this vertex (previous neighbour → vertex) and OUT of it
    // (vertex → next neighbour), with wrap-around for closed paths.
    const inCurve =
      idx - 1 >= 0
        ? (curves[idx - 1] ?? null)
        : path.closed
          ? (curves[curves.length - 1] ?? null)
          : null;
    const outCurve = idx < curves.length ? (curves[idx] ?? null) : null;

    const addSegmentRay = (
      curve: paper.Curve | null,
      neighbor: paper.Point | undefined,
    ) => {
      // Only straight segments — a bezier curve has no single "segment angle".
      if (!curve || curve.hasHandles() || !neighbor) return;
      const dx = newPos.x - neighbor.x;
      const dy = newPos.y - neighbor.y;
      if (Math.hypot(dx, dy) < 1e-6) return;
      let deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
      if (deg < 0) deg += 360;
      rays.push({
        from: { x: neighbor.x, y: neighbor.y },
        to: { x: newPos.x, y: newPos.y },
        angleDeg: deg, // new segment vs horizontal ("zero")
        cornerPrev: { x: oldPos.x, y: oldPos.y }, // arc from the old segment
        cornerPrevGhost: true, // draw the old segment as a ghost ray
      });
    };
    // in-curve's far end (point1) is the previous neighbour; out-curve's far
    // end (point2) is the next neighbour.
    addSegmentRay(inCurve, inCurve?.point1);
    addSegmentRay(outCurve, outCurve?.point2);
    return rays;
  }

  private getAnchorLockMode(spec: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
  }): "angle" | "smooth" | null {
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === spec.constructionId,
    );
    const entry = (line?.smoothAnchors ?? []).find(
      (a) =>
        a.pathIndex === spec.pathIndex && a.segmentIndex === spec.segmentIndex,
    );
    if (!entry) return null;
    return entry.mode ?? "smooth";
  }

  /** When a tangent on `seg` was just changed via `changedSide`,
   *  compute the value the OPPOSITE tangent should take to satisfy
   *  the active lock mode at that anchor:
   *   - "smooth": full mirror — `-changed`
   *   - "angle":  collinear opposite, preserving the opposite side's
   *              current magnitude
   *   - null:    no constraint; returns null so the caller skips the
   *              update
   *  Pulled out as a helper so bezierHandleDrag and curveDrag can
   *  share the same constraint math. */
  private mirroredHandleFor(
    seg: paper.Segment,
    changedSide: "in" | "out",
    mode: "angle" | "smooth" | null,
  ): paper.Point | null {
    // Shared with the piece outline editor (#969) — the lock-mode
    // opposite-tangent math lives in outlineEditGeometry.
    return sharedMirroredHandleFor(
      this.host.getScope(),
      seg,
      changedSide,
      mode,
    );
  }
}
